/**
 * @file index.tsx
 *
 * @fileoverview Renders a AI Job Description button to populate the job description based on
 * information from the job.
 */

import { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFormContext } from 'react-hook-form';
import { Button } from '~/vcl/atoms/01-forms/01-button';
import { PuzzleIcon } from '~/vcl/icons/Puzzle';
import { FormDescription } from '~/vcl/atoms/01-forms/11-form-item/02-form-description';
import { CloseIcon } from '~/vcl/icons/Close';
import { UserContext } from '~/jobiqo/context/user';
import { useChat } from '@ai-sdk/react';
import { DefaultChatTransport } from 'ai';
import type { TextUIPart } from 'ai';
import { AppContext } from '~/jobiqo/context/app';
import { AppContextInterface } from '~/jobiqo/models/app';
import NProgress from 'nprogress';
import {
  getFormattedSalaryRange,
  getSalaryUnitMessage,
} from '~/jobiqo/components/Job/Detail/utils';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';

const JOB_TITLE_FIELD = 'title';
const JOB_DESCRIPTION_FIELD = 'field_job_description';
const JOB_LOCATION = 'field_job_location';
const JOB_SKILLS = 'field_job_skills';
const EMPLOYMENT_TYPE = 'field_job_employment_type';
const SALARY_RANGE = 'field_job_salary_range';
const SALARY_RANGE_FREE_INPUT = 'field_job_salary_range_free_inpu';

interface JobFormAIGenerateDescriptionProps {}

export function JobFormAIGenerateDescription({}: JobFormAIGenerateDescriptionProps) {
  const appContext: AppContextInterface = useContext(AppContext);
  const { JOBS } = useFrontendConfig();
  const { setValue, watch } = useFormContext();
  const { currentOrganizationProfile } = useContext(UserContext);
  const intl = useIntl();
  const temperature = JOBS.AI.JOB_DESCRIPTION.temperature;
  const maxTokens = JOBS.AI.JOB_DESCRIPTION.maxTokens;
  const languages = JOBS.AI.JOB_DESCRIPTION.languages;
  const watchTitle = watch(JOB_TITLE_FIELD);
  const watchLocation = watch(JOB_LOCATION);
  const watchSkills = watch(JOB_SKILLS);
  const watchEmploymentType = watch(EMPLOYMENT_TYPE);
  const watchSalaryRange = watch(SALARY_RANGE);
  const watchSalaryRangeFreeInput = watch(SALARY_RANGE_FREE_INPUT);
  const watchApplicationWorkflow = watch('field_job_application_workflow');
  const formattedSalaryRange = getFormattedSalaryRange(
    watchSalaryRangeFreeInput ?? {},
    intl,
  );
  const salaryUnitMessage = getSalaryUnitMessage(
    watchSalaryRangeFreeInput?.salaryUnit ?? '',
    intl,
  );
  const salaryRangeFreeInputFormatted = `${formattedSalaryRange} ${salaryUnitMessage}`.trim();

  const [AIDescription, setAIDescription] = useState<string | null>(null);

  const prepareJobDescriptionData = useMemo(
    () => () => ({
      jobTitle: watchTitle,
      locations: watchLocation?.join(', '),
      companyName: currentOrganizationProfile?.name,
      benefits: currentOrganizationProfile?.benefits
        ?.map((benefit) => benefit?.label)
        .join(', '),
      industry: currentOrganizationProfile?.industry?.label,
      companyPresentation: currentOrganizationProfile?.presentation,
      skills: watchSkills?.map((term) => term.label).join(', '),
      employmentType: watchEmploymentType
        ?.map((employmentType) => employmentType?.label)
        .join(', '),
      salaryRange: watchSalaryRange?.name,
      salaryRangeFreeInput: salaryRangeFreeInputFormatted,
      applicationWorkflow: watchApplicationWorkflow,
    }),
    [
      watchTitle,
      watchLocation,
      currentOrganizationProfile,
      watchSkills,
      watchEmploymentType,
      watchSalaryRange,
      salaryRangeFreeInputFormatted,
      watchApplicationWorkflow,
    ],
  );

  const { messages, setMessages, sendMessage, stop, status } = useChat({
    transport: new DefaultChatTransport({
      api: '/api/openai-job-description',
      body: () => ({
        temperature,
        maxTokens,
        languages,
        locale: appContext.locale,
        ...prepareJobDescriptionData(),
      }),
    }),
  });

  const handleGenerateButtonSubmit = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setMessages([]);
    // Do a fetch to call Open AI
    sendMessage(
      { text: 'Generate job description' },
      { body: { ...prepareJobDescriptionData() } },
    );
  };

  useEffect(() => {
    // The AIDescription?.length > 15 avoids that CKEditor shows for a split second
    // an error to the user. Because if the response has e.g. <h1 (still streaming) it will consider that as
    // an empty string and show the field as having an error.
    if (AIDescription && AIDescription?.length > 15) {
      setValue(JOB_DESCRIPTION_FIELD, AIDescription);
    }
  }, [AIDescription]);

  useEffect(() => {
    const assistantMessage = [...messages]
      .reverse()
      .find((message) => message.role === 'assistant');
    const description = assistantMessage?.parts
      .filter((part): part is TextUIPart => part.type === 'text')
      .map((part) => part.text)
      .join('');

    if (description) {
      setAIDescription(description);
    }
  }, [messages]);

  useEffect(() => {
    if (status === 'streaming' || status === 'submitted') {
      NProgress.start();
    } else {
      NProgress.done();
    }
  }, [status]);

  return (
    <>
      {status === 'streaming' ? (
        <Button onClick={() => stop()} bordered buttonStyle="default">
          <CloseIcon />
          <span>
            <FormattedMessage
              id="ai_generate_job_description_button_stop"
              defaultMessage="Stop generating"
            />
          </span>
        </Button>
      ) : (
        <Button
          type="button"
          buttonStyle="primary"
          bordered={true}
          disabled={!watchTitle}
          onClick={handleGenerateButtonSubmit}
        >
          <PuzzleIcon />
          <span>
            <FormattedMessage
              id="ai_generate_job_description_button"
              defaultMessage="Generate with AI"
            />
          </span>
        </Button>
      )}
      <FormDescription>
        <FormattedMessage
          id="ai_generate_job_description_description"
          defaultMessage="To achieve the best accuracy in generating results, we encourage you to complete as many fields as possible. Nevertheless, you can generate text by simply entering the job title."
        />
      </FormDescription>
    </>
  );
}

export default JobFormAIGenerateDescription;
